import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@shared/api/client'
import { endpoints } from '@shared/api/endpoints'
import { QUERY_CACHE_KEYS } from '@shared/api/queryKeys'
import { buildQueryParams, usePagination } from '@shared/lib'
import type { Balance, Payment, TopUpRequest, TopUpResponse } from '../model/types'

export function useParticipantBalance() {
  return useQuery<Balance>({
    queryKey: QUERY_CACHE_KEYS.payments.participantBalance,
    queryFn: () =>
      apiClient.get<Balance>(endpoints.payments.participantBalance).then((r) => r.data),
  })
}

export function useParticipantPaymentHistory() {
  const { page, pageSize, setTotalItems, resetPage, ...pagination } = usePagination({
    key: QUERY_CACHE_KEYS.payments.participantHistory.join('-'),
  })

  const queryParams = buildQueryParams({ page, page_size: pageSize })

  const query = useQuery<{ results: Payment[]; count: number }>({
    queryKey: [...QUERY_CACHE_KEYS.payments.participantHistory, queryParams],
    queryFn: async () => {
      const r = await apiClient.get<Payment[] | { results: Payment[]; count?: number }>(
        endpoints.payments.participantHistory,
        { params: queryParams }
      )
      if (Array.isArray(r.data)) return { results: r.data, count: r.data.length }
      return { results: r.data.results, count: r.data.count ?? r.data.results.length }
    },
  })

  useEffect(() => {
    if (query.data?.count != null) setTotalItems(query.data.count)
  }, [query.data?.count, setTotalItems])

  return {
    data: query.data?.results ?? [],
    count: query.data?.count ?? 0,
    isLoading: query.isLoading,
    pagination: { page, pageSize, setTotalItems, resetPage, ...pagination },
  }
}

export function useTopUp() {
  const qc = useQueryClient()
  return useMutation<TopUpResponse, unknown, TopUpRequest>({
    mutationFn: (body) =>
      apiClient.post<TopUpResponse>(endpoints.payments.participantTopup, body).then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: QUERY_CACHE_KEYS.payments.participantBalance }),
    // Тост показывает страница: свой на отказ и на ответ без адреса оплаты.
    meta: { silent: true },
  })
}
